import { useEffect, useState } from 'react'
import type { CombatDTO, CombattantDTO } from '@whatremains/shared'
import { EffetObjet, NomObjet } from '../composants/NomObjet'
import { api, ErreurApi } from '../api'
import { IconeObjet } from '../composants/IconeObjet'
import { TypeObjet } from '../composants/TypeObjet'

/**
 * L'écran de combat.
 *
 * Il affiche et il envoie des actions. Il ne calcule rien : ni dégâts, ni
 * disponibilité, ni issue. `disponible` et `empechement` viennent du serveur,
 * qui est seul à connaître les règles.
 */

function Barre({ c, aGauche }: { c: CombattantDTO; aGauche: boolean }) {
  const part = Math.max(0, Math.min(100, (c.pv / c.pvMax) * 100))
  const etats = [
    c.vu ? null : 'non vu',
    c.garde ? 'couvert' : null,
    c.marque ? 'entaillé' : null,
    c.appret ? 'en mesure' : null,
    c.renfort ? 'accompagné' : null,
  ].filter(Boolean)

  return (
    <div className="cellule" style={{ textAlign: aGauche ? 'left' : 'right' }}>
      <span className="etiquette">{aGauche ? 'Toi' : 'En face'}</span>
      <span style={{ fontFamily: 'var(--display)', fontSize: 24, letterSpacing: '.04em' }}>{c.nom}</span>
      <div className="jauge" style={{ marginTop: 10 }}>
        <div style={{ width: `${part}%`, background: part < 30 ? 'var(--r6)' : 'var(--r7)' }} />
      </div>
      <p style={{ fontFamily: 'var(--mono)', fontSize: 11, marginTop: 8 }}>
        {c.pv} / {c.pvMax}
        {c.pointsNom && c.points > 0 && `   ·   ${c.pointsNom} ${c.points}`}
        {aGauche && `   ·   ${c.ressourceNom} ${c.ressource}`}
      </p>
      {etats.length > 0 && (
        <p className="tenu" style={{ fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '.14em', marginTop: 4 }}>
          {etats.join('  ·  ')}
        </p>
      )}
    </div>
  )
}

export function Combat({
  personnageId,
  combatInitial,
  surChangement,
  surSortie,
}: {
  personnageId: string
  combatInitial: CombatDTO
  surChangement: () => void
  surSortie: () => void
}) {
  const [c, setC] = useState<CombatDTO>(combatInitial)
  const [erreur, setErreur] = useState<string | null>(null)
  const [enCours, setEnCours] = useState(false)

  useEffect(() => {
    const bas = document.getElementById('fin-du-journal')
    bas?.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }, [c.journal.length])

  async function agir(competence: string) {
    setErreur(null)
    setEnCours(true)
    try {
      setC(await api.agir(personnageId, competence))
      surChangement()
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : "Quelque chose n'a pas abouti.")
    } finally {
      setEnCours(false)
    }
  }

  async function laisserFiler() {
    setErreur(null)
    setEnCours(true)
    try {
      setC(await api.laisserFiler(personnageId))
      surChangement()
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : "Quelque chose n'a pas abouti.")
    } finally {
      setEnCours(false)
    }
  }

  return (
    <div className="cadre">
      <div className="entete">
        <div className="surtitre">
          {c.fini ? 'Terminé' : `Tour ${c.tour}`} · approche {c.approche}
        </div>
        <h1 style={{ fontSize: 'clamp(30px,5vw,54px)' }}>{c.adversaire.nom}</h1>
      </div>

      <section>
        <div className="grille" style={{ gridTemplateColumns: '1fr 1fr' }}>
          <Barre c={c.joueur} aGauche />
          <Barre c={c.adversaire} aGauche={false} />
        </div>
      </section>

      <section>
        <div className="surtitre">Journal</div>
        <div style={{ maxHeight: 300, overflowY: 'auto', border: '1px solid var(--line)', padding: '14px 18px' }}>
          {c.journal.length === 0 && <p className="tenu">Rien encore.</p>}
          {c.journal.map((l, i) => (
            <p
              key={i}
              style={{
                margin: '0 0 6px',
                fontSize: 14,
                color: l.qui === 'joueur' ? 'var(--ink)' : l.qui === 'monde' ? 'var(--r7)' : 'var(--ink-dim)',
              }}
            >
              <span className="tenu" style={{ fontFamily: 'var(--mono)', fontSize: 10, marginRight: 12 }}>
                {String(l.tour).padStart(2, '0')}
              </span>
              {l.texte}
            </p>
          ))}
          <div id="fin-du-journal" />
        </div>
      </section>

      {erreur && <p className="erreur">{erreur}</p>}

      {c.fini ? (
        <section>
          <div className="surtitre">
            {c.issue === 'victoire' ? 'Il ne se relève pas' : c.issue === 'fuite' ? 'Tu es parti' : 'Tu es tombé'}
          </div>
          {c.issue === 'victoire' && (
            <p style={{ fontFamily: 'var(--mono)', fontSize: 13 }}>
              {c.orGagne} or · {c.xpGagne} expérience
            </p>
          )}
          {/*
            Le butin, qu'on ne montrait pas.

            Enzo, après treize combats gagnés : « j'ai rien looté depuis le
            début, dans aucun contenu ». Il avait pourtant trois pièces et six
            matériaux dans ses sacs. Le serveur envoyait `butin` à chaque fin de
            combat depuis le premier jour, et l'écran le jetait : les pièces
            tombaient en silence.

            Un butin qu'on ne voit pas tomber n'est pas un butin, c'est une
            ligne d'inventaire qu'on découvre trois jours plus tard sans savoir
            d'où elle vient.
          */}
          {c.butin && (
            <div className="butin-tombe">
              <span className="etiquette">Il laisse quelque chose</span>
              {/*
                Et on la VOIT. Le butin arrivait sans image : `ButinDTO` ne
                portait ni famille, ni catégorie, ni matériau, donc il n'y avait
                rien à dessiner. Trois écrans de butin avaient le même défaut,
                chacun avec sa propre forme de données.
              */}
              <span className="butin-glyphe">
                <IconeObjet objet={c.butin} taille={40} />
              </span>
              <NomObjet o={c.butin} />
              <TypeObjet o={c.butin} />
              <EffetObjet effet={c.butin.effetTexte} />
              <span className="tenu">
                {c.butin.palier} · niveau {c.butin.niveauRequis} · c’est dans tes sacs
              </span>
            </div>
          )}
          {c.issue === 'defaite' && <p className="attenue">Tu te relèveras. Pas maintenant.</p>}
          <button className="action" onClick={surSortie}>Revenir</button>
        </section>
      ) : (
        <section>
          <div className="surtitre">À toi</div>
          <div className="grille">
            {c.competences.map((k) => (
              <button
                key={k.code}
                className="choix"
                disabled={!k.disponible || enCours}
                style={{ opacity: k.disponible ? 1 : 0.4, cursor: k.disponible ? 'pointer' : 'default' }}
                onClick={() => void agir(k.code)}
              >
                <span className="role">{k.cout > 0 ? `${k.cout} ${c.joueur.ressourceNom.toLowerCase()}` : 'gratuit'}</span>
                <span className="nom">{k.nom}</span>
                <p style={{ marginTop: 8 }}>{k.texte}</p>
                {k.empechement && (
                  <p className="tenu" style={{ fontFamily: 'var(--mono)', fontSize: 10, marginTop: 8 }}>
                    {k.empechement}
                  </p>
                )}
              </button>
            ))}
          </div>

          <p style={{ marginTop: 22, display: 'flex', gap: 24 }}>
            <button className="discret" disabled={enCours} onClick={() => void agir('fuir')}>
              Se retirer
            </button>
            <button className="discret" disabled={enCours} onClick={() => void laisserFiler()}>
              Laisser filer
            </button>
          </p>
        </section>
      )}
    </div>
  )
}
